import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import { login } from "../redux/slices/user/thunk";
import { useDispatch } from "react-redux";

const LoginScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language == "ar";
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email");
  const [loginError, setLoginError] = useState("");

  //   // Form validation schema
  // const validationSchema = Yup.object({
  //   emailOrPhone: loginMethod === 'email'
  //     ? Yup.string()
  //         .email(t('email_invalid'))
  //         .required(t('email_required'))
  //     : Yup.string()
  //         .matches(/^[0-9]+$/, t('phone_only_numbers'))
  //         .min(10, t('phone_too_short'))
  //         .max(15, t('phone_too_long'))
  //         .required(t('phone_required')),
  //   password: Yup.string()
  //     .min(8, t('password_min'))
  //     .required(t('password_required')),
  //   rememberMe: Yup.boolean()
  // });

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      emailOrPhone: "",
      password: "",
      // rememberMe: false
    },
    validationSchema: Yup.object().shape({
      emailOrPhone: Yup.string().when([], {
        is: () => loginMethod === "email",
        then: () =>
          Yup.string().email(t("email_invalid")).required(t("email_required")),
        otherwise: () =>
          Yup.string()
            // .matches(/^[0-9]+$/, t("phone_only_numbers"))
            // .min(10, t("phone_too_short"))
            // .max(15, t("phone_too_long"))
            .required(t("phone_required")),
      }),
      password: Yup.string()
        .min(6, t("password_min"))
        .required(t("password_required")),
      // rememberMe: Yup.boolean()
    }),
    onSubmit: async (values) => {
      // console.log('Login form submitted:', values);
      const data = {
        email: values.emailOrPhone,
        password: values.password,
      };

      try {
        setLoading(true);
        const response = await dispatch(login({ data })).unwrap();
        console.log("login succeeded, response:", response);
        localStorage.setItem("user", JSON.stringify(response));
        localStorage.setItem("token", JSON.stringify(response?._id));
        console.log("login succeeded, response:", response?.type);
        if (response?.type == "client") navigate("/home");
        if (response?.type == "family") navigate("/familyHome");
        if (response?.type == "admin") navigate("/adminHome");
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error("addNewUser failed:", err);
      }
    },
  });

  let loginImage;
  try {
    loginImage = require("../assets/Logo.png");
  } catch (error) {
    loginImage = null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid
        container
        spacing={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: 3,
          border: "1px solid #d0d0d0",
        }}
      >
        {/* Left Column - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: theme.palette.primary.mainLoginBackground,
            py: 6,
            px: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: 500, mx: "auto" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: theme.palette.blackandWhite.main,
              }}
            >
              {t("Login")}
            </Typography>

            {/* <Typography variant="body1" sx={{ 
              mb: 4,
              color: theme.palette.text.secondary
            }}>
              {t('Login to access your Yozya account.')}
            </Typography> */}

            {loginError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setLoginError("")}
              >
                {loginError}
              </Alert>
            )}

            {/* Email/Phone Toggle
            <ToggleButtonGroup
              value={loginMethod}
              exclusive
              onChange={handleLoginMethodChange}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
                '& .MuiToggleButton-root': {
                  py: 0.5,
                  px: 4,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: '#333',
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: '#1976d2',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="email" sx={{px:2}}>{t("Email")}</ToggleButton>
              <ToggleButton value="phone" sx={{px:2}}>{t("Phone")}</ToggleButton>
            </ToggleButtonGroup> */}

            {/* Login Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* Email/Phone Field */}
              <CustomTextField
                fullWidth
                id="emailOrPhone"
                name="emailOrPhone"
                label={t("Email address")}
                value={formik.values.emailOrPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.emailOrPhone &&
                  Boolean(formik.errors.emailOrPhone)
                }
                helperText={
                  formik.touched.emailOrPhone && formik.errors.emailOrPhone
                }
                isRtl={isArabic}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                  direction: isArabic ? "rtl" : "ltr", // optional, لكن ليس ضرورياً لأن inputProps dir وdefaultRtlSx أو LTR تعالج الاتجاه
                }}
                inputProps={{
                  dir: isArabic ? "rtl" : "ltr",
                }}
              />

              {/* Password Field */}
              <CustomTextField
                fullWidth
                id="password"
                name="password"
                label={t("Password")}
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                isRtl={isArabic}
                inputProps={{
                  dir: isArabic ? "rtl" : "ltr",
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                  direction: isArabic ? "rtl" : "ltr",
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.rememberMe}
                      onChange={formik.handleChange}
                      name="rememberMe"
                      color="primary"
                    />
                  }
                  label={t("Remember me")}
                /> */}

                <Link
                  href="/forgotpassword"
                  sx={{
                    textDecoration: "none",
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                  }}
                >
                  {t("Forgot Password?")}
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                  py: 1.5,
                  mb: 3,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: "8px",
                }}
              >
                {loading ? (
                  <CircularProgress
                    sx={{ color: theme?.palette?.primary?.mainLoginBackground }}
                  />
                ) : (
                  t("Login")
                )}
              </Button>

              {/* Sign Up Link */}
              <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                {t("Don't have an account?")}{" "}
                <Link
                  href="/signup"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                  }}
                >
                  {t("Sign up")}
                </Link>
              </Typography>

              {/* Divider with "OR" */}
              {/* <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3
              }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body1" sx={{ 
                  px: 2, 
                  color: theme.palette.text.secondary
                }}>
                  Or login with
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box> */}
            </form>
          </Box>
        </Grid>

        {/* Right Column - Login Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            flexDirection:"column",
            boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.23)",
          }}
        >
          {loginImage ? (
            <img
              src={loginImage}
              alt="Login"
              style={{
                width: "50%",
                // height: '100%',
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {t("Login Image Placeholder")}
              </Typography>
            </Box>
          )}
              <Typography variant="h6" color="textSecondary">
                {t("productiveFamiliesMarket")}
              </Typography>
        </Grid>
      </Grid>
    </Container>
  );
};

export default LoginScreen;
